import { Ionicons } from '@expo/vector-icons';
import { router, type Href } from 'expo-router';
import React from 'react';
import { Pressable, StyleSheet, View } from 'react-native';

import { Text } from '@/components/ui';
import { getPriorityStyle } from '@/styles';
import { formatBusinessDate, todayBusinessMs } from '@/utils/date-utils';
import type { UpNextItem, UpNextKind } from '@/utils/up-next';

/**
 * One "Up Next" row — shared by the dashboard panel and the full `/todo` page for
 * maintenance staff, so the two cannot drift.
 *
 * The whole row opens the item. There is no in-place action: the work is the job itself,
 * and the detail page is where a maintenance tech starts it.
 */

const DAY_MS = 86_400_000;

const KIND_ICON: Record<UpNextKind, { icon: keyof typeof Ionicons.glyphMap; color: string }> = {
	'work-order': { icon: 'construct-outline', color: '#546e7a' },
	'follow-up': { icon: 'checkmark-circle-outline', color: '#e65100' },
	task: { icon: 'checkmark-circle-outline', color: '#546e7a' },
};

/** Urgent and High only: Medium and Low rank together, so a chip for them would claim an order that isn't there. */
const PRIORITY_CHIP: Record<string, { label: string; level: number }> = {
	urgent: { label: 'Urgent', level: 0 },
	high: { label: 'High', level: 1 },
};

/**
 * The due label for a business-date `dueMs` against today's (Eastern) business date.
 * Both are midnight-UTC markers, so the difference is a whole number of days.
 */
export const upNextDueLabel = (
	dueMs: number | null,
	todayMs: number,
): { text: string; overdue: boolean } | null => {
	if (dueMs === null || !Number.isFinite(dueMs)) return null;
	const days = Math.round((dueMs - todayMs) / DAY_MS);
	if (days < 0) return { text: `${-days} day${days === -1 ? '' : 's'} overdue`, overdue: true };
	if (days === 0) return { text: 'Due today', overdue: false };
	return { text: `Due ${formatBusinessDate(dueMs)}`, overdue: false };
};

const tagsFor = (item: UpNextItem): string[] => {
	if (item.kind === 'follow-up') return ['Follow-up'];
	if (item.kind === 'task') return ['Task'];
	return item.mine ? [] : ['Unassigned'];
};

export const UpNextRow: React.FC<{ item: UpNextItem }> = ({ item }) => {
	const kind = KIND_ICON[item.kind];
	const chip = PRIORITY_CHIP[(item.priority ?? '').toLowerCase()];
	const due = upNextDueLabel(item.dueMs, todayBusinessMs());
	const tags = tagsFor(item);
	const label = [item.title, item.subtitle, chip?.label, due?.text, ...tags].filter(Boolean).join(', ');

	return (
		<Pressable
			onPress={() => router.push(item.href as Href)}
			style={styles.row}
			accessibilityRole="button"
			accessibilityLabel={label}
			testID={`v2-up-next-${item.key}`}
		>
			<Ionicons name={kind.icon} size={18} color={kind.color} />
			<View style={styles.main}>
				<Text numberOfLines={1} style={styles.title}>{item.title}</Text>
				{/* Gmail-style preheader: enough of the request to recognise the job without opening it. */}
				{item.preview ? (
					<Text
						numberOfLines={2}
						ellipsizeMode="tail"
						style={styles.preview}
						testID={`v2-up-next-preview-${item.key}`}
					>
						{item.preview}
					</Text>
				) : null}
				{item.subtitle ? <Text numberOfLines={1} style={styles.sub}>{item.subtitle}</Text> : null}
				<View style={styles.meta}>
					{chip && (
						<View
							style={[styles.chip, { backgroundColor: getPriorityStyle(chip.level).backgroundColor }]}
							testID={`v2-up-next-priority-${item.key}`}
						>
							<Text style={styles.chipText}>{chip.label}</Text>
						</View>
					)}
					{due && (
						<Text
							style={[styles.due, due.overdue && styles.dueOverdue]}
							testID={`v2-up-next-due-${item.key}`}
						>
							{due.text}
						</Text>
					)}
					{tags.map((tag) => (
						<View key={tag} style={styles.tag}>
							<Text style={styles.tagText}>{tag}</Text>
						</View>
					))}
				</View>
			</View>
			<Ionicons name="chevron-forward" size={16} color="#b0bec5" />
		</Pressable>
	);
};

const styles = StyleSheet.create({
	row: {
		flexDirection: 'row',
		alignItems: 'center',
		gap: 10,
		paddingVertical: 10,
		borderBottomWidth: 1,
		borderBottomColor: '#f1f3f4',
	},
	// minWidth 0 so a long title truncates instead of pushing the chevron off the card —
	// RN-Web flex children default to min-width:auto.
	main: { flex: 1, minWidth: 0 },
	title: { fontSize: 14, fontWeight: '600', color: '#263238' },
	// #5d7986: the lightest blue-grey that still clears WCAG AA on white (see TodoRow's `dim`).
	preview: { fontSize: 13, lineHeight: 18, color: '#5d7986', marginTop: 1 },
	sub: { fontSize: 12, color: '#37474f', marginTop: 1 },
	meta: { flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', gap: 6, marginTop: 3 },
	chip: { borderRadius: 4, paddingHorizontal: 6, paddingVertical: 1 },
	chipText: { color: '#fff', fontSize: 11, fontWeight: '700' },
	due: { fontSize: 12, color: '#90a4ae' },
	dueOverdue: { color: '#c62828', fontWeight: '700' },
	tag: { borderRadius: 4, paddingHorizontal: 6, paddingVertical: 1, backgroundColor: '#eceff1' },
	tagText: { fontSize: 11, color: '#546e7a', fontWeight: '600' },
});
